import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Polyline, Tooltip as LTooltip, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { PALETTE } from '../lib/palette';

export interface LatLng { lat: number; lng: number }

// Coloured SVG pins (Leaflet's default marker images don't resolve under bundlers).
const pin = (color: string) => L.divIcon({
  className: '',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
  html: `<svg viewBox="0 0 24 32" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});
const START_ICON = pin(PALETTE.sky[500]);   // blue  — journey start (where the rep set off for this customer)
const SITE_ICON = pin(PALETTE.success[500]);    // green — customer's registered location
const REACHED_ICON = pin(PALETTE.warning[600]); // orange — arrival / check-in point
const LEAVE_ICON = pin(PALETTE.plum[500]);   // violet — departure / check-out point

/** Great-circle distance between two coordinates, in metres. */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export const fmtDist = (m: number) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`);

// Leaflet mis-measures inside an animated Drawer — recompute size, then frame all points.
function FitBounds({ points }: { points: LatLng[] }) {
  const map = useMap();
  const key = points.map((p) => `${p.lat},${p.lng}`).join('|');
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length === 1) {
        map.setView([points[0].lat, points[0].lng], 16);
      } else if (points.length > 1) {
        map.fitBounds(points.map((p) => [p.lat, p.lng]) as any, { padding: [36, 36], maxZoom: 16 });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [map, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

interface MarkerDef { pt: LatLng; icon: L.DivIcon; label: string; key: string }

// Markers that land within OVERLAP_M of each other (e.g. Reached == Customer when the rep checked in
// right at the shop) would otherwise stack into a single pin. Spread each cluster onto a small circle
// so every pin stays visible + clickable; a hairline connector ties it back to the true spot.
const OVERLAP_M = 25;
const FAN_OFFSET = 0.00022; // ≈ 24 m — stays well inside the 100 m geofence, so the story still reads "at the customer"
function fanOut(markers: MarkerDef[]): { markers: (MarkerDef & { disp: LatLng })[]; links: [LatLng, LatLng][] } {
  const groups: number[][] = [];
  markers.forEach((m, i) => {
    const g = groups.find((grp) => distanceMeters(markers[grp[0]].pt, m.pt) < OVERLAP_M);
    if (g) g.push(i); else groups.push([i]);
  });
  const out = markers.map((m) => ({ ...m, disp: m.pt }));
  const links: [LatLng, LatLng][] = [];
  for (const grp of groups) {
    if (grp.length < 2) continue;
    const cLat = grp.reduce((s, i) => s + markers[i].pt.lat, 0) / grp.length;
    const cLng = grp.reduce((s, i) => s + markers[i].pt.lng, 0) / grp.length;
    grp.forEach((idx, k) => {
      const ang = (2 * Math.PI * k) / grp.length - Math.PI / 2;
      const disp = { lat: cLat + FAN_OFFSET * Math.cos(ang), lng: cLng + FAN_OFFSET * Math.sin(ang) };
      out[idx].disp = disp;
      links.push([{ lat: cLat, lng: cLng }, disp]);
    });
  }
  return { markers: out, links };
}

interface Props {
  /** Where the rep set off for this customer (the "Start" mark) — often the previous stop, e.g. a farmer. */
  journeyStart?: LatLng | null;
  /** Arrival / check-in point (the "Reached" mark). */
  reached?: LatLng | null;
  /** Departure / check-out point (the "Leave" mark). Pass null to hide when it's the same as Reached. */
  leave?: LatLng | null;
  /** Customer's registered location — drawn green with the geofence circle for reference. */
  site?: LatLng | null;
  /** Geofence radius (m) drawn around the customer location. */
  geofenceRadius?: number;
  height?: number;
}

/**
 * Per-visit mini map. Tells the visit's spatial story: the rep's journey start (blue) → the customer
 * (green, with geofence) where they arrived (orange) and left (violet). Co-located pins are fanned out
 * so "Reached at the customer" doesn't hide the customer marker. Renders nothing without coordinates.
 */
export function VisitRouteMap({ journeyStart, reached, leave, site, geofenceRadius = 100, height = 240 }: Props) {
  const defs: MarkerDef[] = [];
  if (journeyStart) defs.push({ pt: journeyStart, icon: START_ICON, label: 'Started here', key: 'start' });
  if (site) defs.push({ pt: site, icon: SITE_ICON, label: 'Customer', key: 'site' });
  if (reached) defs.push({ pt: reached, icon: REACHED_ICON, label: 'Reached (checked in)', key: 'reached' });
  if (leave) defs.push({ pt: leave, icon: LEAVE_ICON, label: 'Left (checked out)', key: 'leave' });
  if (!defs.length) return null;

  const truePoints = defs.map((d) => d.pt);
  const { markers, links } = fanOut(defs);
  // The travel leg: from where the rep set off to where they arrived (falls back to the customer).
  const travelTo = reached ?? site ?? null;

  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #eee' }}>
      <MapContainer
        center={[truePoints[0].lat, truePoints[0].lng]}
        zoom={15}
        style={{ height, width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
        <FitBounds points={truePoints} />

        {site && (
          <Circle
            center={[site.lat, site.lng]}
            radius={geofenceRadius}
            pathOptions={{ color: 'var(--color-success-500)', fillColor: PALETTE.success[500], fillOpacity: 0.08, weight: 1 }}
          />
        )}
        {/* The travel path the rep took to get here. */}
        {journeyStart && travelTo && (
          <Polyline
            positions={[[journeyStart.lat, journeyStart.lng], [travelTo.lat, travelTo.lng]]}
            pathOptions={{ color: 'var(--color-sky-500)', dashArray: '6 6', weight: 2 }}
          />
        )}
        {/* Hairline links from a fanned-out pin back to its true (shared) location. */}
        {links.map(([a, b], i) => (
          <Polyline key={`lnk-${i}`} positions={[[a.lat, a.lng], [b.lat, b.lng]]}
            pathOptions={{ color: '#94a3b8', weight: 1, opacity: 0.7 }} />
        ))}

        {markers.map((m) => (
          <Marker key={m.key} position={[m.disp.lat, m.disp.lng]} icon={m.icon}>
            <LTooltip>{m.label}</LTooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
